import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MarketingHeader, MarketingFooter, Primary } from "../components/layout/MarketingShell";
import BlogArt from "../components/blog/BlogArt";
import { CATEGORIES, POSTS, featuredPost, categoryLabel } from "../data/blogPosts";

const PAGE = 3;

function Eyebrow({ children, className = "" }) {
  return (
    <p className={`text-[11.5px] font-semibold uppercase tracking-[0.14em] text-b2-indigo ${className}`}>
      {children}
    </p>
  );
}

function Arrow() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h13M13 6l6 6-6 6" />
    </svg>
  );
}

function PostCard({ post }) {
  return (
    <article className="flex flex-col bg-white border border-b2-line rounded-[14px] overflow-hidden
                        hover:border-b2-linestrong transition-colors">
      <Link to={`/blog/${post.slug}`} tabIndex={-1} aria-hidden="true" className="block h-[190px]">
        <BlogArt name={post.art} className="h-full" />
      </Link>
      <div className="flex flex-col flex-1 p-6">
        <Eyebrow>{post.eyebrow || categoryLabel(post.category)}</Eyebrow>
        <h3 className="mt-3 text-[19px] leading-[1.3] font-bold tracking-[-0.02em] text-b2-ink">
          <Link to={`/blog/${post.slug}`} className="hover:text-b2-indigo transition-colors">
            {post.title}
          </Link>
        </h3>
        <div className="mt-auto pt-5">
          <Link
            to={`/blog/${post.slug}`}
            className="inline-flex items-center gap-2 text-[14.5px] font-semibold text-b2-indigo
                       hover:text-b2-indigohover transition-colors"
          >
            Baca artikel <Arrow />
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function Blog() {
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(PAGE);

  const isSearching = query.trim().length > 0;

  /* When searching, look across everything including the featured post, so a
     search that matches it does not come back empty. */
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = isSearching ? POSTS : POSTS.filter((p) => p !== featuredPost);
    return pool.filter((p) => {
      const inCategory = active === "all" || p.category === active;
      if (!inCategory) return false;
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        categoryLabel(p.category).toLowerCase().includes(q)
      );
    });
  }, [active, query, isSearching]);

  const visible = results.slice(0, shown);
  const hasMore = results.length > visible.length;
  const showFeatured = !isSearching && active === "all";

  function pick(id) {
    setActive(id);
    setShown(PAGE);
  }

  return (
    <div className="b2 min-h-screen bg-b2-canvas text-b2-ink font-sans">
      <MarketingHeader />

      <main>
        {/* Hero */}
        <section className="max-w-[1120px] mx-auto px-6 pt-10 pb-2">
          <Eyebrow>Blog Bicarra</Eyebrow>
          <div className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div className="max-w-[560px]">
              <h1 className="text-[42px] sm:text-[48px] leading-[1.08] font-bold tracking-[-0.035em]">
                Idea ringkas.<br />Perbualan lebih baik.
              </h1>
              <p className="mt-4 text-[16.5px] leading-relaxed text-b2-inksoft">
                Panduan praktikal untuk membantu pelanggan dengan AI yang beradab.
              </p>
            </div>

            <div className="w-full lg:w-[380px] shrink-0">
              <label htmlFor="blog-search" className="sr-only">Cari artikel</label>
              <div className="relative">
                <svg viewBox="0 0 24 24"
                     className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-b2-faint"
                     fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-3.2-3.2" />
                </svg>
                <input
                  id="blog-search"
                  type="search"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setShown(PAGE); }}
                  placeholder="Cari artikel..."
                  data-testid="blog-search"
                  className="w-full h-[52px] pl-11 pr-4 rounded-[12px] bg-white border border-b2-line
                             text-[15px] text-b2-ink placeholder:text-b2-faint
                             focus:outline-none focus:border-b2-indigo transition-colors"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="max-w-[1120px] mx-auto px-6 pt-8">
          <nav className="flex flex-wrap items-center gap-2" aria-label="Kategori artikel">
            {CATEGORIES.map((c) => {
              const on = c.id === active;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pick(c.id)}
                  aria-pressed={on}
                  data-testid={`blog-cat-${c.id}`}
                  className={`h-9 px-4 rounded-[9px] text-[14px] font-medium transition-colors ${
                    on
                      ? "bg-b2-wash text-b2-washink font-semibold"
                      : "text-b2-inksoft hover:text-b2-ink"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </nav>
        </section>

        {/* Featured */}
        {showFeatured && (
          <section className="max-w-[1120px] mx-auto px-6 pt-6">
            <article className="bg-white border border-b2-line rounded-[16px] overflow-hidden
                                grid grid-cols-1 lg:grid-cols-2">
              <div className="p-3 lg:p-4">
                <BlogArt name={featuredPost.art} className="h-[260px] lg:h-full min-h-[260px] rounded-[12px] overflow-hidden" />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <Eyebrow>{featuredPost.eyebrow}</Eyebrow>
                <h2 className="mt-4 text-[30px] sm:text-[34px] leading-[1.18] font-bold tracking-[-0.03em]">
                  {featuredPost.title}
                </h2>
                <p className="mt-4 text-[16px] leading-relaxed text-b2-inksoft">
                  {featuredPost.excerpt}
                </p>
                <Link
                  to={`/blog/${featuredPost.slug}`}
                  data-testid="blog-featured-link"
                  className="mt-7 inline-flex items-center gap-2 text-[15px] font-semibold text-b2-indigo
                             hover:text-b2-indigohover transition-colors"
                >
                  Baca panduan <Arrow />
                </Link>
              </div>
            </article>
          </section>
        )}

        {/* Listing */}
        <section className="max-w-[1120px] mx-auto px-6 pt-14 pb-4">
          <h2 className="text-[27px] font-bold tracking-[-0.03em]">
            {isSearching ? "Hasil carian" : "Bacaan terkini"}
          </h2>

          {visible.length > 0 ? (
            <div className="mt-7 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.map((p) => <PostCard key={p.slug} post={p} />)}
            </div>
          ) : (
            <div className="mt-7 bg-white border border-b2-line rounded-[14px] py-16 px-6 text-center">
              <p className="text-[16px] font-semibold">Tiada artikel dijumpai.</p>
              <p className="mt-2 text-[14.5px] text-b2-inksoft">
                Cuba kata kunci lain, atau lihat semua artikel.
              </p>
              <button
                type="button"
                onClick={() => { setQuery(""); pick("all"); }}
                className="mt-5 text-[14.5px] font-semibold text-b2-indigo hover:text-b2-indigohover"
              >
                Lihat semua artikel
              </button>
            </div>
          )}

          {hasMore && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => setShown((n) => n + PAGE)}
                data-testid="blog-load-more"
                className="inline-flex items-center justify-center h-11 px-6 rounded-[10px] bg-white
                           border border-b2-linestrong text-b2-ink text-[15px] font-semibold
                           hover:border-b2-indigo transition-colors"
              >
                Lihat lagi artikel
              </button>
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="max-w-[1120px] mx-auto px-6 pt-10 pb-16">
          <div className="bg-b2-wash rounded-[16px] px-8 sm:px-12 py-12
                          flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <h2 className="text-[29px] leading-[1.2] font-bold tracking-[-0.03em]">
                Daripada membaca,<br />kepada mencuba.
              </h2>
              <p className="mt-3 text-[16px] text-b2-inksoft">
                Bina pembantu AI untuk perniagaan anda.
              </p>
            </div>
            <div className="flex flex-col items-start lg:items-center gap-2.5 shrink-0">
              <Primary to="/register" testid="blog-cta-register">Mula sekarang</Primary>
              <p className="text-[13px] text-b2-faint">Tanpa perlu menulis kod.</p>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
